import { useState, useEffect, useCallback } from 'react'
import { SchoolAnnouncementService } from '../../services/schoolAnnouncements'
import { GroupService } from '../../services/groups'
import type { SchoolAnnouncement, SchoolAnnouncementInsert } from '../../services/schoolAnnouncements'
import type { GroupWithMemberCount } from '../../services/groups'
import { UserService } from '../../services/users'
import { toLocalDateTimeString } from '../../utils/datetimeLocal'
import { compareRosterValue } from '../../utils/gradeClass'

// 学校の生徒の学年／クラスに、編集中のお知らせで保存済みの値（卒業した学年など）を足した選択肢
const mergeOptions = (schoolValues: string[], saved: string[] = []) =>
  Array.from(new Set([...schoolValues, ...saved])).sort(compareRosterValue)

interface FormData {
  title: string
  content: string
  url: string
  published_at: string
  image?: File
  target_grades: string[]
  target_classes: string[]
  target_group_ids: string[]
}

function formatTargetSummary(ann: SchoolAnnouncement, groups: GroupWithMemberCount[]): string {
  const parts: string[] = []
  if (ann.target_grades.length > 0) parts.push(ann.target_grades.join('・'))
  if (ann.target_classes.length > 0) parts.push(ann.target_classes.join('・'))
  if (ann.target_group_ids.length > 0) {
    const names = ann.target_group_ids
      .map(id => groups.find(g => g.id === id)?.name)
      .filter(Boolean) as string[]
    if (names.length > 0) parts.push(names.join('・'))
  }
  return parts.length > 0 ? parts.join(' / ') : '全生徒'
}

interface Props {
  schoolId: string
}

const SchoolAnnouncementManager = ({ schoolId }: Props) => {
  const [announcements, setAnnouncements] = useState<SchoolAnnouncement[]>([])
  const [groups, setGroups] = useState<GroupWithMemberCount[]>([])
  const [schoolGrades, setSchoolGrades] = useState<string[]>([])
  const [schoolClasses, setSchoolClasses] = useState<string[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState<SchoolAnnouncement | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  const [formData, setFormData] = useState<FormData>({
    title: '',
    content: '',
    url: '',
    published_at: toLocalDateTimeString(),
    image: undefined,
    target_grades: [],
    target_classes: [],
    target_group_ids: [],
  })

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true)
      const [announcementsData, groupsData, gradeClassOptions] = await Promise.all([
        SchoolAnnouncementService.getAllSchoolAnnouncements(schoolId),
        GroupService.getSchoolGroups(schoolId),
        UserService.getSchoolGradeClassOptions(schoolId),
      ])
      setAnnouncements(announcementsData)
      setGroups(groupsData)
      setSchoolGrades(gradeClassOptions.grades)
      setSchoolClasses(gradeClassOptions.classes)
    } catch (err) {
      console.error('Failed to load school announcements:', err)
      setError('お知らせの読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [schoolId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      url: '',
      published_at: toLocalDateTimeString(),
      image: undefined,
      target_grades: [],
      target_classes: [],
      target_group_ids: [],
    })
    setEditingItem(null)
    setImagePreview(null)
    setError('')
  }

  const handleAdd = () => {
    resetForm()
    setShowForm(true)
  }

  const handleEdit = (ann: SchoolAnnouncement) => {
    setFormData({
      title: ann.title,
      content: ann.content,
      url: ann.url || '',
      published_at: toLocalDateTimeString(ann.published_at),
      image: undefined,
      target_grades: ann.target_grades,
      target_classes: ann.target_classes,
      target_group_ids: ann.target_group_ids,
    })
    setEditingItem(ann)
    setImagePreview(ann.image_path ? SchoolAnnouncementService.getImageUrl(ann.image_path) : null)
    setShowForm(true)
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('画像ファイルを選択してください')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('ファイルサイズは5MB以下にしてください')
      return
    }
    setFormData(prev => ({ ...prev, image: file }))
    const reader = new FileReader()
    reader.onload = (e) => setImagePreview(e.target?.result as string)
    reader.readAsDataURL(file)
  }

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: undefined }))
    setImagePreview(null)
  }

  const gradeOptions = mergeOptions(schoolGrades, editingItem?.target_grades)
  const classOptions = mergeOptions(schoolClasses, editingItem?.target_classes)

  const toggleGrade = (grade: string) => {
    setFormData(prev => ({
      ...prev,
      target_grades: prev.target_grades.includes(grade)
        ? prev.target_grades.filter(g => g !== grade)
        : [...prev.target_grades, grade],
    }))
  }

  const toggleClass = (cls: string) => {
    setFormData(prev => ({
      ...prev,
      target_classes: prev.target_classes.includes(cls)
        ? prev.target_classes.filter(c => c !== cls)
        : [...prev.target_classes, cls],
    }))
  }

  const toggleGroup = (groupId: string) => {
    setFormData(prev => ({
      ...prev,
      target_group_ids: prev.target_group_ids.includes(groupId)
        ? prev.target_group_ids.filter(id => id !== groupId)
        : [...prev.target_group_ids, groupId],
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim() || !formData.content.trim()) {
      setError('タイトルと内容は必須です')
      return
    }
    setIsSaving(true)
    setError('')

    try {
      let imagePath: string | undefined

      if (formData.image) {
        imagePath = await SchoolAnnouncementService.uploadImage(formData.image)
      } else if (editingItem) {
        imagePath = editingItem.image_path
      }

      const payload: SchoolAnnouncementInsert = {
        school_id: schoolId,
        title: formData.title.trim(),
        content: formData.content.trim(),
        url: formData.url.trim() || undefined,
        image_path: imagePath,
        target_grades: formData.target_grades,
        target_classes: formData.target_classes,
        target_group_ids: formData.target_group_ids,
        published_at: new Date(formData.published_at).toISOString(),
      }

      if (editingItem) {
        // Delete old image if a new one was uploaded
        if (formData.image && editingItem.image_path) {
          try {
            await SchoolAnnouncementService.deleteImage(editingItem.image_path)
          } catch {
            console.warn('Failed to delete old image')
          }
        }
        await SchoolAnnouncementService.updateSchoolAnnouncement(editingItem.id, payload)
      } else {
        await SchoolAnnouncementService.createSchoolAnnouncement(payload)
      }

      setShowForm(false)
      resetForm()
      await loadData()
    } catch (err) {
      console.error('Failed to save school announcement:', err)
      setError(err instanceof Error ? err.message : 'お知らせの保存に失敗しました')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (ann: SchoolAnnouncement) => {
    if (!confirm(`「${ann.title}」を削除しますか？この操作は取り消せません。`)) return

    try {
      setIsLoading(true)
      if (ann.image_path) {
        try {
          await SchoolAnnouncementService.deleteImage(ann.image_path)
        } catch {
          console.warn('Failed to delete image')
        }
      }
      await SchoolAnnouncementService.deleteSchoolAnnouncement(ann.id)
      await loadData()
    } catch (err) {
      console.error('Failed to delete school announcement:', err)
      setError(err instanceof Error ? err.message : 'お知らせの削除に失敗しました')
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })

  const isPublished = (ann: SchoolAnnouncement) =>
    new Date(ann.published_at) <= new Date()

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">お知らせ管理</h2>
        <button
          onClick={handleAdd}
          disabled={isLoading}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
        >
          新しいお知らせを追加
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 text-sm">{error}</div>
          <button onClick={() => setError('')} className="mt-2 text-red-600 hover:text-red-800 text-sm underline">
            閉じる
          </button>
        </div>
      )}

      {/* Add / Edit Form */}
      {showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingItem ? 'お知らせを編集' : '新しいお知らせを作成'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Target audience */}
            <div className="bg-white border border-gray-200 rounded-md p-4">
              <div className="flex gap-6">
                <div className="text-sm font-medium text-gray-700 whitespace-nowrap pt-1">
                  対象者<br />
                  <span className="font-normal text-gray-500">(複数選択可)</span>
                </div>
                <div className="space-y-3 flex-1">
                  {/* Grade */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-bold text-gray-800 w-12">学年</span>
                    <label className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.target_grades.length === 0}
                        onChange={() => setFormData(prev => ({ ...prev, target_grades: [] }))}
                      />
                      全て
                    </label>
                    {gradeOptions.length === 0 ? (
                      <span className="text-sm text-gray-400">学年データなし</span>
                    ) : (
                      gradeOptions.map(grade => (
                        <label key={grade} className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.target_grades.includes(grade)}
                            onChange={() => toggleGrade(grade)}
                          />
                          {grade}
                        </label>
                      ))
                    )}
                  </div>

                  {/* Class */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-bold text-gray-800 w-12">クラス</span>
                    <label className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.target_classes.length === 0}
                        onChange={() => setFormData(prev => ({ ...prev, target_classes: [] }))}
                      />
                      全て
                    </label>
                    {classOptions.length === 0 ? (
                      <span className="text-sm text-gray-400">クラスデータなし</span>
                    ) : (
                      classOptions.map(cls => (
                        <label key={cls} className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.target_classes.includes(cls)}
                            onChange={() => toggleClass(cls)}
                          />
                          {cls}
                        </label>
                      ))
                    )}
                  </div>

                  {/* Group */}
                  {groups.length > 0 && (
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-sm font-bold text-gray-800 w-12">グループ</span>
                      {groups.map(group => (
                        <label key={group.id} className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.target_group_ids.includes(group.id)}
                            onChange={() => toggleGroup(group.id)}
                          />
                          {group.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">タイトル *</label>
              <input
                type="text"
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">内容 *</label>
              <textarea
                value={formData.content}
                onChange={e => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">画像</label>
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {imagePreview && (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-md border"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">外部リンクURL</label>
              <input
                type="url"
                value={formData.url}
                onChange={e => setFormData(prev => ({ ...prev, url: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>

            {/* Publish date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">公開日時 *</label>
              <input
                type="datetime-local"
                value={formData.published_at}
                onChange={e => setFormData(prev => ({ ...prev, published_at: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isSaving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
              >
                {isSaving ? '保存中...' : editingItem ? '更新' : '作成'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); resetForm() }}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md text-sm font-medium"
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Announcements List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {isLoading && announcements.length === 0 ? (
          <div className="p-6 text-center text-gray-500">読み込み中...</div>
        ) : announcements.length === 0 ? (
          <div className="p-6 text-center text-gray-500">お知らせがありません</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {announcements.map(ann => (
              <li key={ann.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-lg font-medium text-gray-900 truncate">{ann.title}</h3>
                      {!isPublished(ann) && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          未公開
                        </span>
                      )}
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
                        {formatTargetSummary(ann, groups)}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-2 line-clamp-2">{ann.content}</p>
                    {ann.image_path && (
                      <img
                        src={SchoolAnnouncementService.getImageUrl(ann.image_path)}
                        alt=""
                        className="w-16 h-16 object-cover rounded-md mb-2"
                      />
                    )}
                    {ann.url && (
                      <p className="text-blue-600 text-sm mb-2">
                        <a href={ann.url} target="_blank" rel="noopener noreferrer">{ann.url}</a>
                      </p>
                    )}
                    <div className="text-sm text-gray-500">
                      公開日時: {formatDate(ann.published_at)} | 作成日時: {formatDate(ann.created_at)}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(ann)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDelete(ann)}
                      className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded text-sm"
                    >
                      削除
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default SchoolAnnouncementManager
